import { Checkbox, Input, Select, Space, Typography, theme } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import { CompetitionLabel } from "components/Shared/Labels/CompetitionLabel";
import { PersonLabel } from "components/Shared/Labels/PersonLabel";
import { SeasonLabel } from "components/Shared/Labels/SeasonLabel";
import { SuspendedRosterStatusPopup } from "components/Shared/SuspendedRosterStatusPopup/SuspendedRosterStatusPopup.tsx";
import { TaskStatusTag } from "components/Shared/TaskStatusTag";
import { PeopleTypes } from "enums/peopleTypes";
import type { PersonStatuses } from "enums/personStatuses";
import type { Roles } from "enums/roles";
import { RosterStatuses } from "enums/rosterStatuses";
import { debounce } from "lodash-es";
import moment from "moment";
import { useIntl } from "react-intl";
import { getTaskRejectReason } from "utils/getTaskRejectReason";
import { DATE_FORMAT, TIME_FORMAT } from "~app/constants";
import { useSportConfiguration } from "./useSportConfiguration";

const { useToken } = theme;

type EditableColumnDefinition = {
  readonly?: (record: any) => boolean;
  disabled?: (record: any) => boolean;
  onChange?: (value: any, record: any) => void;
};

export const useSharedColumnBuilder = () => {
  const { token } = useToken();
  const { formatMessage } = useIntl();
  const { configuration } = useSportConfiguration();
  const createDebouncedHandler = (lambda: (value: any) => void) =>
    debounce((value) => {
      lambda(value);
    }, 500);

  const checkIfReadonly = (columnDefinition: EditableColumnDefinition, record: any) => {
    if (typeof columnDefinition.readonly === "function") {
      return columnDefinition.readonly(record);
    }
    return columnDefinition.readonly ?? true;
  };
  const checkIfDisabled = (columnDefinition: EditableColumnDefinition, record: any) => {
    if (typeof columnDefinition.disabled === "function") {
      return columnDefinition.disabled(record);
    }
    return columnDefinition.disabled ?? true;
  };

  const createCheckBoxColumn = (titleIntlKey, fieldName, width, columnDefinition: EditableColumnDefinition) => ({
    title: formatMessage({ id: titleIntlKey }),
    dataIndex: fieldName,
    width: width,
    render: (value: boolean, record: any) => {
      if (checkIfReadonly(columnDefinition, record)) {
        return value ? <i className="fa-solid fa-check" /> : "";
      }
      return (
        <Checkbox
          disabled={checkIfDisabled(columnDefinition, record)}
          checked={value}
          onChange={(e) => columnDefinition?.onChange?.(e.target.checked, record)}
        />
      );
    },
    align: "center" as const,
  });

  const createPersonNameColumn = ({ checkIfAlreadyAdded }: { checkIfAlreadyAdded?: (record: any) => boolean }) => ({
    title: formatMessage({ id: "columns.staff.name" }),
    dataIndex: "nameFullLocal",
    render: (_, record: any) => <PersonLabel person={record} checkIfAlreadyAdded={checkIfAlreadyAdded} />,
  });

  const createPersonDobColumn = ({ withSorter }: { withSorter: boolean }) => ({
    title: formatMessage({ id: "columns.person.dob" }),
    width: 100,
    dataIndex: "dob",
    align: "center" as const,
    render: (value?: string) => (value ? moment(value).format("DD-MM-YYYY") : ""),
    sorter: withSorter
      ? (a: any, b: any) => {
          if (a?.dob && b?.dob) {
            return moment(a.dob).unix() - moment(b.dob).unix();
          }
          if (!a?.dob && b?.dob) {
            return -1;
          }
          if (a?.dob && !b?.dob) {
            return 1;
          }
          return 0;
        }
      : null,
  });

  const createPersonNationalityColumn = () => ({
    title: formatMessage({ id: "columns.person.nationality" }),
    dataIndex: "nationality",
    align: "center" as const,
    width: 75,
  });

  const createGenderColumn = () => ({
    title: formatMessage({ id: "columns.gender" }),
    dataIndex: "gender",
    align: "center" as const,
    render: (gender) => (gender ? formatMessage({ id: `gender.${gender}` }) : ""),
    width: 75,
  });

  const createPersonStatusColumn = () => ({
    title: formatMessage({ id: "columns.person.status" }),
    dataIndex: "personStatus",
    width: 100,
    render: (status?: PersonStatuses | null) =>
      status ? formatMessage({ id: `personStatus.${status}` }).toUpperCase() : "",
    align: "center" as const,
  });

  const createPersonIsActiveColumn = (columnDefinition: EditableColumnDefinition) =>
    createCheckBoxColumn("columns.person.isActive", "isActive", 75, columnDefinition);

  const createRosterNameColumn = ({
    withSorter,
    handlePersonImageClick,
  }: {
    withSorter: boolean;
    handlePersonImageClick?: (record: any) => void;
  }) => ({
    title: formatMessage({ id: "columns.roster.name" }),
    dataIndex: "nameFullLocal",
    defaultSortOrder: "ascend" as const,
    render: (_, record: any) => <PersonLabel person={record} onImageClick={handlePersonImageClick} />,
    sorter: withSorter ? (a: any, b: any) => a?.nameFullLocal?.localeCompare(b?.nameFullLocal || "") : null,
  });

  const createRosterStatusColumn = () => ({
    title: formatMessage({ id: "columns.roster.status" }),
    dataIndex: "seasonRosterPlayerStatus",
    render: (status?: RosterStatuses | null, record?: any) => {
      if (!status) return "";
      const label = formatMessage({ id: `rosterStatus.${status}` }).toUpperCase();
      if (status === RosterStatuses.Suspended) {
        return (
          <Space size={4}>
            {label}
            <SuspendedRosterStatusPopup fixtureRosterPlayer={record} />
          </Space>
        );
      }
      return label;
    },
    align: "center" as const,
    width: 175,
  });

  const createRosterPositionColumn = (columnDefinition: EditableColumnDefinition) => ({
    title: formatMessage({ id: "columns.roster.position" }),
    width: 120,
    dataIndex: "position",
    render: (value: string, record: any) => {
      if (checkIfReadonly(columnDefinition, record)) {
        return value ? formatMessage({ id: `position.${value}` }) : "";
      }

      return (
        <Select
          disabled={checkIfDisabled(columnDefinition, record)}
          style={{ width: "150px" }}
          showSearch
          defaultValue={value}
          placeholder={formatMessage({
            id: "select.rosterPosition.placeholder",
          })}
          filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())}
          onChange={(x) => columnDefinition?.onChange?.(x, record)}
          options={configuration.rosterPositions.map((x) => ({
            value: x,
            label: formatMessage({ id: `position.${x}` }),
            dcPersonId: record?.dcPersonId,
            personId: record?.personId,
          }))}
        />
      );
    },
    align: "center" as const,
  });

  const createRosterNumberColumn = (columnDefinition: EditableColumnDefinition & { withSorter: boolean }) => ({
    title: formatMessage({ id: "columns.roster.number" }),
    width: 100,
    dataIndex: "number",
    fixed: "left" as const,
    render: (value: number, record: any) => {
      if (checkIfReadonly(columnDefinition, record)) {
        return value;
      }
      return (
        <Input
          style={{ textAlign: "center" }}
          disabled={checkIfDisabled(columnDefinition, record)}
          defaultValue={value}
          onInput={createDebouncedHandler((e) =>
            columnDefinition?.onChange?.(e.target.value?.toString() ?? "", record)
          )}
        />
      );
    },
    align: "center" as const,
    sorter: columnDefinition?.withSorter
      ? (a: any, b: any) => parseInt(a?.number || "0", 10) - parseInt(b?.number || "0", 10)
      : null,
  });

  const createRosterIsCaptainColumn = (columnDefinition: EditableColumnDefinition) =>
    createCheckBoxColumn("columns.roster.isCaptain", "isCaptain", 75, columnDefinition);

  const createRosterIsStarterColumn = (columnDefinition: EditableColumnDefinition) =>
    createCheckBoxColumn("columns.roster.isStarter", "isStarter", 75, columnDefinition);

  const createStaffNameColumn = ({
    withSorter,
    handlePersonImageClick,
  }: {
    withSorter: boolean;
    handlePersonImageClick?: (record: any) => void;
  }) => ({
    title: formatMessage({ id: "columns.staff.name" }),
    dataIndex: "nameFullLocal",
    defaultSortOrder: "ascend" as const,
    render: (_, record: any) => <PersonLabel person={record} onImageClick={handlePersonImageClick} />,
    sorter: withSorter ? (a: any, b: any) => a?.nameFullLocal?.localeCompare(b?.nameFullLocal || "") : null,
  });

  const createSeasonStaffRoleColumn = (columnDefinition: EditableColumnDefinition) =>
    createRoleColumn(configuration.seasonStaffRoles, columnDefinition);

  const createFixtureStaffRoleColumn = (columnDefinition: EditableColumnDefinition) =>
    createRoleColumn(configuration.fixtureStaffRoles, columnDefinition);

  const createRoleColumn = (roles: Roles[], columnDefinition: EditableColumnDefinition) => ({
    title: formatMessage({ id: "columns.staff.role" }),
    width: 250,
    dataIndex: "role",
    render: (value: string, record: any) => {
      if (checkIfReadonly(columnDefinition, record)) {
        return value ? formatMessage({ id: `role.${value}` }) : "";
      }

      return (
        <Select
          disabled={checkIfDisabled(columnDefinition, record)}
          style={{ width: "150px" }}
          showSearch
          defaultValue={value ? formatMessage({ id: `role.${value}` }) : value}
          placeholder={formatMessage({ id: "select.staffRole.placeholder" })}
          filterOption={(input, option) => (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())}
          onChange={(x) => columnDefinition?.onChange?.(x, record)}
          options={roles.map((x) => {
            return {
              value: x,
              label: formatMessage({ id: `role.${x}` }),
              dcPersonId: record?.dcPersonId,
              personId: record?.personId,
            };
          })}
        />
      );
    },
    align: "center" as const,
  });

  const createCompetitionNameColumn = () => ({
    title: formatMessage({ id: "columns.competition.name" }),
    dataIndex: "competitionNameLocal",
    width: 300,
    render: (value, record: any) => <CompetitionLabel title={value} imageUrl={record?.competitionImageUrl} />,
  });

  const createSeasonNameColumn = (dataIndex: string = "nameLocal") => ({
    title: formatMessage({ id: "columns.season.name" }),
    dataIndex,
    width: 300,
    render: (value, record) => <SeasonLabel title={value} imageUrl={record?.imageUrl} />,
  });

  const createSeasonStartDateColumn = () => ({
    title: formatMessage({ id: "columns.season.startDate" }),
    dataIndex: "startDate",
    width: 100,
    render: (value) => (value ? moment(value).format(DATE_FORMAT) : ""),
    align: "center",
  });

  const createSeasonEndDateColumn = () => ({
    title: formatMessage({ id: "columns.season.endDate" }),
    dataIndex: "endDate",
    width: 100,
    render: (value) => (value ? moment(value).format(DATE_FORMAT) : ""),
    align: "center",
  });

  const createSeasonAgeGroupColumn = () => ({
    title: formatMessage({ id: "columns.season.ageGroup" }),
    dataIndex: "ageGroup",
    width: 100,
    render: (ageGroup) => formatMessage({ id: `ageGroup.${ageGroup}` }),
    align: "center",
  });

  const createSeasonYearColumn = () => ({
    title: formatMessage({ id: "columns.season.year" }),
    dataIndex: "year",
    width: 50,
    align: "center",
  });

  const createSeasonStatusColumn = () => ({
    dataIndex: "status",
    title: formatMessage({ id: "columns.season.status" }),
    width: 100,
    align: "center",
  });

  const createTaskStatusColumn = () => ({
    dataIndex: "taskStatus",
    title: formatMessage({ id: "columns.task.status" }),
    width: 120,
    align: "center",
    render: (taskStatus, record) => <TaskStatusTag rejectReason={getTaskRejectReason(record)} status={taskStatus} />,
  });

  const createFixtureStatusColumn = () => ({
    dataIndex: "status",
    title: formatMessage({ id: "columns.fixture.status" }),
    width: 100,
    align: "center",
  });

  const createFixtureRoundColumn = () => ({
    dataIndex: "round",
    align: "center",
    title: formatMessage({ id: "columns.fixture.round" }),
    width: 70,
  });

  const createFixtureStartDateColumn = () => ({
    dataIndex: "startDateTimeLocal",
    align: "center",
    title: formatMessage({ id: "columns.fixture.startDate" }),
    render: (value) => (value ? moment(value).format(DATE_FORMAT) : ""),
    width: 70,
  });

  const createFixtureStartTimeColumn = () => ({
    dataIndex: "startDateTimeLocal",
    align: "center",
    title: formatMessage({ id: "columns.fixture.startTime" }),
    width: 70,
    render: (value) => (value ? moment(value).format(TIME_FORMAT) : ""),
  });

  const createFixtureResultColumn = () => ({
    title: formatMessage({ id: "columns.fixture.result" }),
    align: "center",
    render: ({ homeEntity, awayEntity }) => {
      if (!awayEntity || !homeEntity) {
        return "";
      }

      if (!homeEntity?.score && !homeEntity?.secondaryScore) {
        return "";
      }
      if (!awayEntity?.score && !awayEntity?.secondaryScore) {
        return "";
      }

      let isHomeEntityWin = false;
      let isAwayEntityWin = false;

      const homeResult = parseInt(homeEntity?.score, 10);
      const awayResult = parseInt(awayEntity?.score, 10);

      const homeSecondaryResult = parseInt(homeEntity?.secondaryScore, 10);
      const awaySecondaryResult = parseInt(awayEntity?.secondaryScore, 10);

      if (homeResult > awayResult) {
        isHomeEntityWin = true;
        isAwayEntityWin = false;
      }

      if (homeResult < awayResult) {
        isHomeEntityWin = false;
        isAwayEntityWin = true;
      }

      if (homeSecondaryResult && awaySecondaryResult) {
        if (homeResult + homeSecondaryResult > awayResult + awaySecondaryResult) {
          isHomeEntityWin = true;
          isAwayEntityWin = false;
        }

        if (homeResult + homeSecondaryResult < awayResult + awaySecondaryResult) {
          isHomeEntityWin = false;
          isAwayEntityWin = true;
        }

        return (
          <Space size={3}>
            {isHomeEntityWin && <i className="fa-solid fa-caret-left mr-5"></i>}
            <Typography.Text strong={isHomeEntityWin}>
              {homeResult} ({homeSecondaryResult})
            </Typography.Text>
            <Typography.Text>:</Typography.Text>
            <Typography.Text strong={isAwayEntityWin}>
              ({awaySecondaryResult}) {awayResult}
            </Typography.Text>
            {isAwayEntityWin && <i className="fa-solid fa-caret-right ml-5"></i>}
          </Space>
        );
      }

      return (
        <Space size={3}>
          {isHomeEntityWin && <i className="fa-solid fa-caret-left mr-5"></i>}
          <Typography.Text strong={isHomeEntityWin}>{homeResult}</Typography.Text>
          <Typography.Text>:</Typography.Text>
          <Typography.Text strong={isAwayEntityWin}>{awayResult}</Typography.Text>
          {isAwayEntityWin && <i className="fa-solid fa-caret-right ml-5"></i>}
        </Space>
      );
    },
    width: 120,
  });

  const createFixtureVenueColumn = () => ({
    title: formatMessage({ id: "columns.fixture.venue" }),
    dataIndex: "venue",
    align: "center",
    render: (value) => value?.nameLocal,
    width: 100,
  });

  const createFixtureStageColumn = () => ({
    title: formatMessage({ id: "columns.fixture.stage" }),
    dataIndex: "stage",
    align: "center",
    render: (value) => value?.nameLocal,
    width: 100,
  });

  const createFixturePoolColumn = () => ({
    title: formatMessage({ id: "columns.fixture.pool" }),
    dataIndex: "pool",
    align: "center",
    render: (value) => value?.nameLocal,
    width: 100,
  });

  const createFixtureSeriesColumn = () => ({
    title: formatMessage({ id: "columns.fixture.series" }),
    dataIndex: "series",
    align: "center",
    render: (value) => value?.nameLocal,
    width: 100,
  });

  const createIsRegistrationOpen = () => ({
    dataIndex: "isRegistrationOpened",
    title: formatMessage({ id: "columns.isRegistrationOpened" }),
    width: 100,
    render: (isRegistrationOpened) =>
      isRegistrationOpened ? (
        <i style={{ color: token.colorSuccess }} className="fa-solid fa-check"></i>
      ) : (
        <i style={{ color: token.colorError }} className="fa-solid fa-xmark"></i>
      ),
    align: "center",
  });

  const createCopyPeopleActionsColumn = (
    type: PeopleTypes,
    onPreview: (record: any) => void,
    onCopy: (record: any) => void
  ) => ({
    width: 50,
    align: "right",
    render: (record: any) => (
      <Space size="small">
        <DefaultButton
          onClick={() => onPreview(record)}
          type="text"
          title={formatMessage({
            id:
              type === PeopleTypes.Roster
                ? "button.previewRoster"
                : type === PeopleTypes.Staff
                  ? "button.previewStaff"
                  : "",
          })}
          icon={<i className="fa-solid fa-eye" />}
        />
        <DefaultButton
          onClick={() => onCopy(record)}
          type="text"
          title={formatMessage({
            id:
              type === PeopleTypes.Roster ? "button.copyRoster" : type === PeopleTypes.Staff ? "button.copyStaff" : "",
          })}
          icon={<i className="fa-solid fa-clone" />}
        />
      </Space>
    ),
  });

  return {
    createPersonNameColumn,
    createPersonDobColumn,
    createPersonNationalityColumn,
    createPersonStatusColumn,
    createPersonIsActiveColumn,
    createGenderColumn,

    createRosterStatusColumn,
    createRosterNumberColumn,
    createRosterPositionColumn,
    createRosterNameColumn,
    createRosterIsCaptainColumn,
    createRosterIsStarterColumn,

    createStaffNameColumn,
    createSeasonStaffRoleColumn,
    createFixtureStaffRoleColumn,

    createCompetitionNameColumn,
    createSeasonNameColumn,
    createSeasonStartDateColumn,
    createSeasonEndDateColumn,
    createSeasonAgeGroupColumn,
    createSeasonYearColumn,
    createSeasonStatusColumn,

    createFixtureStatusColumn,
    createFixtureRoundColumn,
    createFixtureStartDateColumn,
    createFixtureStartTimeColumn,
    createFixtureResultColumn,
    createFixtureVenueColumn,
    createFixtureStageColumn,
    createFixturePoolColumn,
    createFixtureSeriesColumn,

    createTaskStatusColumn,
    createIsRegistrationOpen,
    createCopyPeopleActionsColumn,
  };
};
